import React, { useEffect, useMemo, useState } from "react";
import { getAllVotes, getUserBallots } from "../../../services/voteServices";
import { toast } from "react-toastify";
import useScrollToTop from "../../../hooks/useScollToTop";
import { useUser } from "./../../Home/UserContext";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import ParticipateVoteModal from "./ParticipateVoteModal";
import { useVote } from "../../../hooks/VoteContext";

/* ================= Helpers ================= */
const StatusPill = ({ status }) => {
  const map = {
    actif: "bg-emerald-50 text-emerald-700 ring-emerald-200",
    a_venir: "bg-amber-50 text-amber-700 ring-amber-200",
    termine: "bg-slate-100 text-slate-700 ring-slate-200",
    inactif: "bg-rose-50 text-rose-700 ring-rose-200",
  };
  const label = {
    actif: "En cours",
    a_venir: "À venir",
    termine: "Terminé",
    inactif: "Inactif",
  }[status];
  return (
    <span
      className={`inline-flex items-center gap-2 px-2.5 py-1 rounded-full text-xs font-medium ring-1 ${
        map[status] || "bg-slate-100 text-slate-700 ring-slate-200"
      }`}
    >
      {label}
    </span>
  );
};

const TypePill = ({ type, isAnonymous }) => {
  const base = type === "sondage" ? "Sondage" : "Vote";
  const modeLabel = isAnonymous === false ? "nominatif" : "anonyme";
  const cls =
    isAnonymous === false
      ? "bg-amber-50 text-amber-700 ring-amber-200"
      : "bg-emerald-50 text-emerald-700 ring-emerald-200";

  return (
    <span
      className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium ring-1 ${cls}`}
    >
      <span>{base}</span>
      <span className="opacity-80">• {modeLabel}</span>
    </span>
  );
};

const computeStatus = (vote) => {
  const now = dayjs();
  const start = dayjs(vote.start_time);
  const end = dayjs(vote.end_time);
  if (!vote.is_active) return "inactif";
  if (now.isBefore(start)) return "a_venir";
  if (now.isAfter(end)) return "termine";
  return "actif";
};

// Normalise allowed_roles (null | [] | "[4,5]" | ["4","5"]) -> number[]
const parseAllowedRoles = (val) => {
  try {
    if (val == null) return [];
    if (Array.isArray(val))
      return val.map((x) => Number(x)).filter(Number.isFinite);
    if (typeof val === "string") {
      const parsed = JSON.parse(val);
      if (Array.isArray(parsed))
        return parsed.map((x) => Number(x)).filter(Number.isFinite);
    }
  } catch (_) {}
  return [];
};
/* =========================================== */

const UserVotePage = () => {
  useScrollToTop();
  const navigate = useNavigate();
  const { user } = useUser();
  const { syncPendingVotes } = useVote();
  const userId = user?.userId;
  const userRoleId = Number(user?.role_id); // ← rôle courant

  const [votes, setVotes] = useState([]);
  const [ballots, setBallots] = useState([]);
  const [selectedVoteId, setSelectedVoteId] = useState(null);
  const [openModal, setOpenModal] = useState(false);
  const [filter, setFilter] = useState("actif"); // actif | a_venir | termine | tous


  const fetchVotesAndBallots = async () => {
  try {
    const [voteData, ballotData] = await Promise.all([
      getAllVotes(),
      userId ? getUserBallots(userId) : Promise.resolve([]),
    ]);

    const normalizedVotes = Array.isArray(voteData)
      ? voteData
      : [];

    const normalizedBallots = Array.isArray(ballotData)
      ? ballotData
      : [];

    setVotes(normalizedVotes);
    setBallots(normalizedBallots);

    syncPendingVotes(
      normalizedVotes,
      normalizedBallots,
    );
  } catch (error) {
    console.error(
      "Erreur lors du chargement des votes :",
      error,
    );

    toast.error(
      "Erreur lors du chargement des votes.",
    );
  }
};

  useEffect(() => {
    fetchVotesAndBallots();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  const hasVoted = (voteId) => ballots.find((b) => b.vote_id === voteId);

  // Filtre visibilité par rôle + filtre statut
  const filtered = useMemo(() => {
    const canSeeVote = (v) => {
      const roles = parseAllowedRoles(v.allowed_roles);
      if (roles.length === 0) return true; // visible pour tous
      if (!Number.isFinite(userRoleId)) return false; // pas de rôle => pas d'accès aux restreints
      return roles.includes(userRoleId);
    };

    const rows = (votes || [])
      .filter(canSeeVote) // ← filtre audience
      .map((v) => ({ ...v, _status: computeStatus(v) }))
      .sort(
        (a, b) => dayjs(b.start_time).valueOf() - dayjs(a.start_time).valueOf()
      );

    if (filter === "tous") return rows;
    return rows.filter((v) => v._status === filter);
  }, [votes, filter, userRoleId]);

  const handleParticipate = (voteId) => {
    setSelectedVoteId(voteId);
    setOpenModal(true);
  };
  const handleCloseModal = () => {
    setSelectedVoteId(null);
    setOpenModal(false);
  };

  return (
    <div className="w-full pt-[70px] font-satoshi">
      {/* Hero image */}
      <div className="relative h-[260px] sm:h-[320px]">
        <img
          src="/img/cover/voteelectronique.jpg"
          alt=""
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/30 to-black/70" />
        <div className="relative z-10 max-w-[1280px] mx-auto h-full flex flex-col justify-end px-4 pb-6 text-white">
          <nav className="text-xs sm:text-sm opacity-90 mb-1">
            <span
              onClick={() => navigate("/")}
              className="cursor-pointer hover:underline"
            >
              Accueil
            </span>{" "}
            &gt; Espace Utilisateur &gt; Votes
          </nav>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">
            Votes & Sondages
          </h1>
          <p className="text-white/90 text-sm sm:text-base mt-1">
            Participez aux consultations officielles de votre structure.
          </p>
        </div>
      </div>

      {/* Toolbar */}
      <div className="max-w-[1280px] mx-auto px-4 mt-4 relative z-10">
        <div className="rounded-2xl bg-white/95 dark:bg-slate-800/95 ring-1 ring-slate-200 dark:ring-slate-700 p-3 sm:p-4 shadow-lg">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div className="flex flex-wrap items-center gap-2">
              <button
                onClick={() => setFilter("actif")}
                className={`px-3 h-9 rounded-xl ring-1 ${
                  filter === "actif"
                    ? "bg-emerald-600 text-white ring-emerald-600"
                    : "ring-slate-300 hover:bg-slate-50 dark:ring-slate-600 dark:hover:bg-slate-700/60"
                }`}
              >
                En cours
              </button>
              <button
                onClick={() => setFilter("a_venir")}
                className={`px-3 h-9 rounded-xl ring-1 ${
                  filter === "a_venir"
                    ? "bg-amber-600 text-white ring-amber-600"
                    : "ring-slate-300 hover:bg-slate-50 dark:ring-slate-600 dark:hover:bg-slate-700/60"
                }`}
              >
                À venir
              </button>
              <button
                onClick={() => setFilter("termine")}
                className={`px-3 h-9 rounded-xl ring-1 ${
                  filter === "termine"
                    ? "bg-slate-900 text-white ring-slate-900"
                    : "ring-slate-300 hover:bg-slate-50 dark:ring-slate-600 dark:hover:bg-slate-700/60"
                }`}
              >
                Terminés
              </button>
              <button
                onClick={() => setFilter("tous")}
                className={`px-3 h-9 rounded-xl ring-1 ${
                  filter === "tous"
                    ? "bg-indigo-600 text-white ring-indigo-600"
                    : "ring-slate-300 hover:bg-slate-50 dark:ring-slate-600 dark:hover:bg-slate-700/60"
                }`}
              >
                Tous
              </button>
            </div>
            <div className="text-sm text-slate-600 dark:text-slate-300">
              {filtered.length} élément{filtered.length > 1 ? "s" : ""}
            </div>
          </div>
        </div>
      </div>

      {/* Grid */}
      <div className="max-w-[1280px] mx-auto px-4 mt-6 mb-10">
        {filtered.length === 0 ? (
          <div className="rounded-xl bg-white dark:bg-slate-800 ring-1 ring-slate-200 dark:ring-slate-700 p-8 text-center text-slate-500">
            Aucun vote disponible pour votre rôle avec ce filtre.
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {filtered.map((vote) => {
              const ballot = hasVoted(vote.id);
              const status = computeStatus(vote);
              const start = dayjs(vote.start_time).format("DD/MM/YYYY");
              const end = dayjs(vote.end_time).format("DD/MM/YYYY");
              return (
                <article
                  key={vote.id}
                  className="group relative rounded-2xl bg-white dark:bg-slate-900 ring-1 ring-slate-200 dark:ring-slate-700 overflow-hidden hover:shadow-xl transition"
                >
                  <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-indigo-500 via-violet-500 to-fuchsia-500" />
                  <div className="p-4 sm:p-5">
                    <div className="flex items-center justify-between gap-2 mb-3">
                      <TypePill
                        type={vote.type}
                        isAnonymous={vote.is_anonymous}
                      />
                      <StatusPill status={status} />
                    </div>

                    <h3 className="text-lg font-bold text-slate-900 dark:text-white">
                      {vote.title}
                    </h3>
                    {vote.description ? (
                      <p className="mt-1 text-sm text-slate-600 dark:text-slate-300 line-clamp-3">
                        {vote.description}
                      </p>
                    ) : null}

                    <div className="mt-3 text-xs text-slate-500">
                      Période :{" "}
                      <span className="font-medium text-slate-700 dark:text-slate-200">
                        {start}
                      </span>{" "}
                      →{" "}
                      <span className="font-medium text-slate-700 dark:text-slate-200">
                        {end}
                      </span>
                    </div>

                    <div className="mt-1 text-[11px] text-slate-500">
                      {vote.type === "sondage" ? "Sondage" : "Vote"}{" "}
                      {vote.is_anonymous === false
                        ? "nominatif : votre identité pourra être visible des organisateurs dans les résultats détaillés."
                        : "anonyme : vos réponses seront comptabilisées sans affichage de votre identité dans les résultats publics."}
                    </div>

                    <div className="mt-4 flex items-center justify-between">
                      {ballot ? (
                        <div className="text-xs rounded-lg bg-slate-100 dark:bg-slate-800 px-2.5 py-1 text-slate-700 dark:text-slate-200">
                          Répondu le{" "}
                          {dayjs(ballot.timestamp).format("DD/MM/YYYY")}
                        </div>
                      ) : (
                        <div className="text-xs text-slate-500">
                          {status === "actif"
                            ? "Ouvert"
                            : status === "a_venir"
                            ? "Bientôt disponible"
                            : "Clôturé"}
                        </div>
                      )}

                      <button
                        disabled={!!ballot || status !== "actif"}
                        onClick={() => handleParticipate(vote.id)}
                        className={`px-3 h-9 rounded-xl transition ${
                          ballot || status !== "actif"
                            ? "bg-slate-200 text-slate-500 cursor-not-allowed"
                            : "bg-indigo-600 text-white hover:bg-indigo-700"
                        }`}
                      >
                        {ballot ? "Déjà répondu" : "Répondre"}
                      </button>
                    </div>
                  </div>
                </article>
              );
            })}
          </div>
        )}
      </div>

      {/* Modal */}
      {openModal && selectedVoteId && (
        <ParticipateVoteModal
          isOpen={openModal}
          voteId={selectedVoteId}
          userId={userId}
          onClose={handleCloseModal}
          refreshVotes={fetchVotesAndBallots}
        />
      )}
    </div>
  );
};

export default UserVotePage;
